import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { uploadFile } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImagePlus, MapPin, Pencil, Plus, TreePalm, Trash2, X } from "lucide-react";

const MAX_IMAGES = 8;
const EMPTY = {
  name: "", city: "", about: "", description: "", images: [], rooms: [], amenities: [],
  experiences: [], nearby: "", booking_url: "", rating_text: "", contact_email: "",
  contact_phone: "", active: true, order: 0,
};

const Section = ({ title, hint, children }) => (
  <div className="border-t border-border pt-5">
    <div className="overline">{title}</div>
    {hint ? <p className="text-xs text-muted-foreground mt-1 mb-3">{hint}</p> : <div className="mb-3" />}
    {children}
  </div>
);

/** Admin → Retreats: partner resort listings shown on the Natural Retreat pages. */
export const RetreatsTab = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/retreats").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const patch = (fields) => setForm((f) => ({ ...f, ...fields }));

  const upload = async (file, onDone) => {
    setBusy(true);
    try {
      const { url } = await uploadFile(file, "retreat");
      onDone(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const room = MAX_IMAGES - form.images.length;
    if (room <= 0) return toast.error(`A property holds up to ${MAX_IMAGES} photos`);
    for (const f of files.slice(0, room)) {
      // eslint-disable-next-line no-await-in-loop
      await upload(f, (url) => setForm((prev) => ({ ...prev, images: [...prev.images, url] })));
    }
  };

  const save = async () => {
    if (!form.name.trim() || !form.city.trim()) return toast.error("Property name and city are needed");
    setBusy(true);
    try {
      const body = { ...form, order: Number(form.order) || 0 };
      if (editingId) await api.put(`/admin/retreats/${editingId}`, body);
      else await api.post("/admin/retreats", body);
      toast.success(editingId ? "Listing updated" : "Listing added");
      setOpen(false); setForm(EMPTY); setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/retreats/${id}`);
      toast.success("Listing removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const edit = (r) => {
    setForm({ ...EMPTY, ...r, images: r.images || [], rooms: r.rooms || [], amenities: r.amenities || [], experiences: r.experiences || [] });
    setEditingId(r.id);
    setOpen(true);
  };

  const listRow = (key, index, fields) => {
    const next = [...form[key]];
    next[index] = { ...next[index], ...fields };
    patch({ [key]: next });
  };

  return (
    <Card className="border-border" data-testid="retreats-tab">
      <CardContent className="p-6 lg:p-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h3 className="font-display text-2xl flex items-center gap-2"><TreePalm size={18} /> Retreat partners</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Showcase listings for the Natural Retreat page. Booking stays on the partner's own site.
            </p>
          </div>
          <Button onClick={() => { setForm(EMPTY); setEditingId(null); setOpen(true); }} className="btn-action" data-testid="retreat-new">
            <Plus size={14} className="mr-1.5" /> Add property
          </Button>
        </div>

        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-4 rounded-2xl border border-border p-4" data-testid={`retreat-admin-row-${r.id}`}>
              <div className="w-20 h-16 rounded-xl bg-muted overflow-hidden shrink-0">
                {r.images?.[0] ? <img src={mediaUrl(r.images[0])} alt="" className="w-full h-full object-cover" /> : null}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display text-xl truncate">{r.name}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                  <MapPin size={11} /> {r.city} · {r.rooms?.length || 0} rooms · {r.amenities?.length || 0} amenities · {r.images?.length || 0} photos
                </div>
              </div>
              {!r.active && <Badge variant="outline" className="text-[10px]">hidden</Badge>}
              {!r.booking_url && <Badge variant="outline" className="text-[10px] text-secondary">no booking link</Badge>}
              <button onClick={() => edit(r)} className="p-2 text-muted-foreground hover:text-primary" title="Edit" data-testid={`retreat-edit-${r.id}`}>
                <Pencil size={14} />
              </button>
              <button onClick={() => remove(r.id)} className="p-2 text-destructive" title="Remove" data-testid={`retreat-del-${r.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {rows.length === 0 && <p className="text-sm text-muted-foreground py-4" data-testid="retreats-empty">No partner properties yet.</p>}
        </div>

        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditingId(null); } }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{editingId ? "Edit property" : "New property"}</DialogTitle>
            </DialogHeader>

            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="overline">Property name</Label>
                  <Input value={form.name} onChange={(e) => patch({ name: e.target.value })} className="mt-2 bg-transparent" data-testid="retreat-f-name" />
                </div>
                <div>
                  <Label className="overline">Location / city</Label>
                  <Input value={form.city} onChange={(e) => patch({ city: e.target.value })} className="mt-2 bg-transparent" data-testid="retreat-f-city" />
                </div>
              </div>
              <div>
                <Label className="overline">About (short)</Label>
                <Textarea rows={2} value={form.about} onChange={(e) => patch({ about: e.target.value })} className="mt-2 bg-transparent" data-testid="retreat-f-about" />
              </div>
              <div>
                <Label className="overline">Full description</Label>
                <Textarea rows={5} value={form.description} onChange={(e) => patch({ description: e.target.value })} className="mt-2 bg-transparent" data-testid="retreat-f-description" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="overline">Booking URL (Book Now button)</Label>
                  <Input value={form.booking_url} onChange={(e) => patch({ booking_url: e.target.value })} placeholder="https://…" className="mt-2 bg-transparent" data-testid="retreat-f-booking" />
                </div>
                <div>
                  <Label className="overline">Rating text (optional)</Label>
                  <Input value={form.rating_text} onChange={(e) => patch({ rating_text: e.target.value })} placeholder="4.6 · 2719 reviews" className="mt-2 bg-transparent" data-testid="retreat-f-rating" />
                </div>
                <div>
                  <Label className="overline">Enquiry email (property)</Label>
                  <Input value={form.contact_email} onChange={(e) => patch({ contact_email: e.target.value })} className="mt-2 bg-transparent" data-testid="retreat-f-email" />
                </div>
                <div>
                  <Label className="overline">Sort order</Label>
                  <Input type="number" value={form.order} onChange={(e) => patch({ order: e.target.value })} className="mt-2 bg-transparent" data-testid="retreat-f-order" />
                </div>
              </div>

              <Section title={`Photos (${form.images.length}/${MAX_IMAGES})`} hint="The first photo is the cover used on the listing page and the detail hero.">
                <div className="flex flex-wrap gap-3">
                  {form.images.map((img, i) => (
                    <div key={i} className="relative w-28 h-20 rounded-xl overflow-hidden border border-border" data-testid={`retreat-photo-${i}`}>
                      <img src={mediaUrl(img)} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => patch({ images: form.images.filter((_, n) => n !== i) })}
                        className="absolute top-1 right-1 bg-background/85 rounded-full p-1 text-destructive"
                        data-testid={`retreat-photo-remove-${i}`}
                      >
                        <X size={11} />
                      </button>
                      {i === 0 && <span className="absolute bottom-1 left-1 text-[9px] bg-primary text-primary-foreground px-1.5 rounded">cover</span>}
                    </div>
                  ))}
                  {form.images.length < MAX_IMAGES && (
                    <label className="w-28 h-20 rounded-xl border border-dashed border-border flex flex-col items-center justify-center text-muted-foreground cursor-pointer hover:border-primary/50" data-testid="retreat-photo-add">
                      <ImagePlus size={16} />
                      <span className="text-[10px] mt-1">{busy ? "Uploading…" : "Add photo"}</span>
                      <input type="file" accept="image/*" multiple hidden onChange={addPhotos} />
                    </label>
                  )}
                </div>
              </Section>

              <Section title="Room types" hint="Each room shows as a card with its own photo and one-line description.">
                <div className="space-y-3">
                  {form.rooms.map((room, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-3 rounded-xl border border-border p-3" data-testid={`retreat-room-row-${i}`}>
                      <label className="w-16 h-14 rounded-lg bg-muted overflow-hidden shrink-0 cursor-pointer flex items-center justify-center">
                        {room.image ? <img src={mediaUrl(room.image)} alt="" className="w-full h-full object-cover" /> : <ImagePlus size={14} className="text-muted-foreground" />}
                        <input type="file" accept="image/*" hidden onChange={(e) => {
                          const f = e.target.files?.[0]; e.target.value = "";
                          if (f) upload(f, (url) => listRow("rooms", i, { image: url }));
                        }} />
                      </label>
                      <Input value={room.name} onChange={(e) => listRow("rooms", i, { name: e.target.value })} placeholder="Room name" className="bg-transparent w-48" data-testid={`retreat-room-name-${i}`} />
                      <Input value={room.description} onChange={(e) => listRow("rooms", i, { description: e.target.value })} placeholder="Short description" className="bg-transparent flex-1 min-w-[180px]" />
                      <button onClick={() => patch({ rooms: form.rooms.filter((_, n) => n !== i) })} className="text-destructive p-1" data-testid={`retreat-room-del-${i}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
                <Button variant="outline" onClick={() => patch({ rooms: [...form.rooms, { name: "", description: "", image: "" }] })} className="btn-pill border-border mt-3" data-testid="retreat-room-add">
                  <Plus size={13} className="mr-1.5" /> Add room type
                </Button>
              </Section>

              <Section title="Amenities" hint="One per line, or comma separated — shown as an icon grid.">
                <Textarea
                  rows={3}
                  value={form.amenities.join(", ")}
                  onChange={(e) => patch({ amenities: e.target.value.split(/[\n,]/).map((a) => a.trim()).filter(Boolean) })}
                  className="bg-transparent"
                  data-testid="retreat-f-amenities"
                />
              </Section>

              <Section title="Resort experiences" hint="Two to four highlights, each with a photo, a title and one paragraph.">
                <div className="space-y-3">
                  {form.experiences.map((x, i) => (
                    <div key={i} className="flex flex-wrap items-start gap-3 rounded-xl border border-border p-3" data-testid={`retreat-exp-row-${i}`}>
                      <label className="w-16 h-14 rounded-lg bg-muted overflow-hidden shrink-0 cursor-pointer flex items-center justify-center">
                        {x.image ? <img src={mediaUrl(x.image)} alt="" className="w-full h-full object-cover" /> : <ImagePlus size={14} className="text-muted-foreground" />}
                        <input type="file" accept="image/*" hidden onChange={(e) => {
                          const f = e.target.files?.[0]; e.target.value = "";
                          if (f) upload(f, (url) => listRow("experiences", i, { image: url }));
                        }} />
                      </label>
                      <div className="flex-1 min-w-[220px] space-y-2">
                        <Input value={x.title} onChange={(e) => listRow("experiences", i, { title: e.target.value })} placeholder="Highlight title" className="bg-transparent" data-testid={`retreat-exp-title-${i}`} />
                        <Textarea rows={2} value={x.description} onChange={(e) => listRow("experiences", i, { description: e.target.value })} placeholder="One paragraph" className="bg-transparent" />
                      </div>
                      <button onClick={() => patch({ experiences: form.experiences.filter((_, n) => n !== i) })} className="text-destructive p-1" data-testid={`retreat-exp-del-${i}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
                {form.experiences.length < 4 && (
                  <Button variant="outline" onClick={() => patch({ experiences: [...form.experiences, { title: "", description: "", image: "" }] })} className="btn-pill border-border mt-3" data-testid="retreat-exp-add">
                    <Plus size={13} className="mr-1.5" /> Add experience
                  </Button>
                )}
              </Section>

              <Section title="Nearby attractions">
                <Textarea rows={4} value={form.nearby} onChange={(e) => patch({ nearby: e.target.value })} className="bg-transparent" data-testid="retreat-f-nearby" />
              </Section>

              <div className="flex items-center justify-between rounded-xl border border-border p-3">
                <span className="text-sm">Visible on the website</span>
                <Switch checked={form.active} onCheckedChange={(v) => patch({ active: v })} data-testid="retreat-f-active" />
              </div>

              <Button onClick={save} disabled={busy} className="btn-action w-full" data-testid="retreat-save">
                {busy ? "Saving…" : editingId ? "Save changes" : "Add property"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default RetreatsTab;
